import * as d3 from 'd3';
import React, { useEffect, useRef, useState } from 'react';

const LAPColor = getComputedStyle(document.documentElement).getPropertyValue('--background-lowAttenuation-plaque').trim();
const NCColor = getComputedStyle(document.documentElement).getPropertyValue('--background-nonCalcified-plaque').trim();
const CColor = getComputedStyle(document.documentElement).getPropertyValue('--background-calcified-plaque').trim();
const LumenColor = getComputedStyle(document.documentElement).getPropertyValue('--background-lumen').trim();

interface BarChartData {
  label: string;
  value: number;
}

interface MultiBarChartProps {
  data: BarChartData[];
  width: number;
  height: number;
}

const MultiBarChart: React.FC<MultiBarChartProps> = ({ data, width, height }) => {
  const ref = useRef<SVGSVGElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (!ref.current) return;

    const svg = d3.select(ref.current);
    svg.selectAll('*').remove(); // 초기화

    const rootStyle = getComputedStyle(document.documentElement);
    const density = document.documentElement.dataset.displayDensity;
    const runtimeScale = Number.parseFloat(
      rootStyle.getPropertyValue('--display-typography-scale'),
    );
    const safeScale = Number.isFinite(runtimeScale) && runtimeScale > 0 ? runtimeScale : 1;
    const isCompactViewport =
      density !== '4k' &&
      typeof window !== 'undefined' &&
      (window.innerWidth <= 1600 || window.innerHeight <= 1000);
    const isCompactChart = density !== '4k' && (isCompactViewport || width <= 135 || height <= 54);
    const labelFontSize = density === '4k'
      ? 12.5
      : density === 'large'
        ? 11.5
        : isCompactChart
          ? 7.2
          : isCompactViewport
            ? 9.2
            : 11;
    const percentFontSize = density === '4k'
      ? 12.5
      : density === 'large'
        ? 11.5
        : isCompactChart
          ? 7.2
          : isCompactViewport
            ? 9.2
            : 11;
    const barHeight = Math.round(
      (density === '4k' ? 16 : isCompactChart ? 9 : isCompactViewport ? 12 : 15) *
        Math.min(safeScale, isCompactChart ? 1.04 : 1.18),
    );
    const barSpacing = Math.round(barHeight + (density === '4k' ? 4 : isCompactChart ? 2 : isCompactViewport ? 2 : 3));
    const labelMap: Record<string, string> = isCompactChart
      ? {
          'Low attenuation Plaque': 'Low atten.',
          'Noncalcified Plaque': 'Noncalc.',
          'Calcified Plaque': 'Calcified',
        }
      : {};
    const colors = [LAPColor, NCColor, CColor, LumenColor];
    const outlineOffsets: Array<[number, number]> = [
      [-0.5, -0.5],
      [0.5, -0.5],
      [-0.5, 0.5],
      [0.5, 0.5],
    ];

    const appendOutlinedPercentText = (x: number, y: number, text: string) => {
      outlineOffsets.forEach(([dx, dy]) => {
        svg
          .append('text')
          .attr('x', x + dx)
          .attr('y', y + dy)
          .attr('fill', '#000')
          .attr('font-size', `${percentFontSize}px`)
          .attr('font-weight', 'bold')
          .style('pointer-events', 'none')
          .style('text-shadow', 'none')
          .style('filter', 'none')
          .text(text);
      });

      svg
        .append('text')
        .attr('x', x)
        .attr('y', y)
        .attr('fill', 'white')
        .attr('font-size', `${percentFontSize}px`)
        .attr('font-weight', 'bold')
        .style('pointer-events', 'none')
        .style('text-shadow', 'none')
        .style('filter', 'none')
        .text(text);
    };

    data.forEach((d, i) => {
      const y = i * barSpacing;
      const barWidth = Math.max(48, width * (isCompactChart ? 0.56 : 0.68));
      const filledWidth = (d.value / 100) * barWidth;

      svg
        .append('rect')
        .attr('x', 0)
        .attr('y', y)
        .attr('width', barWidth)
        .attr('height', barHeight)
        .attr('fill', '#ccc')
        .attr('fill-opacity', 0.3);

      svg
        .append('rect')
        .attr('x', 0)
        .attr('y', y)
        .attr('width', filledWidth)
        .attr('height', barHeight)
        .attr('fill', colors[i % colors.length]);

      svg
        .append('text')
        .attr('x', 1)
        .attr('y', y + Math.round(barHeight * 0.72))
        .attr('fill', '#111')
        .attr('font-size', `${labelFontSize}px`)
        .attr('font-weight', 'bold')
        .style('text-shadow', 'none')
        .style('filter', 'none')
        .text(labelMap[d.label] ?? d.label);

      appendOutlinedPercentText(
        barWidth + (isCompactChart ? 3 : 6),
        y + barHeight / 2 + Math.round(percentFontSize * 0.28),
        `${d.value.toFixed(1)}%`,
      );
    });
  }, [data, width, height]);

  const colors = [LAPColor, NCColor, CColor, LumenColor];
  const style = {
    '--bar-chart-width': `${width}px`,
    '--bar-chart-height': `${height}px`,
  } as React.CSSProperties;

  return (
    <div
      className={`plaque-bar-hover-shell ${isExpanded ? 'is-expanded' : ''}`}
      style={style}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
      onFocus={() => setIsExpanded(true)}
      onBlur={() => setIsExpanded(false)}
      tabIndex={0}
    >
      <div className="plaque-bar-compact" aria-hidden="true">
        <div className="plaque-bar-color-stack">
          {data.map((d, i) => (
            <span
              key={`${d.label}-color`}
              className="plaque-bar-color-segment"
              style={{ backgroundColor: colors[i % colors.length] }}
            />
          ))}
        </div>
        <div className="plaque-bar-percent-stack">
          {data.map((d) => (
            <span key={`${d.label}-value`} className="plaque-bar-percent-value">
              {`${d.value.toFixed(1)}%`}
            </span>
          ))}
        </div>
      </div>
      <svg className="plaque-bar-expanded" ref={ref} width={width} height={height} />
    </div>
  );
};

export default MultiBarChart;
